import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import BookDemoButton from './components/BookDemoButton';
import Breadcrumbs from './components/Breadcrumbs';

const navLinks = [
  { name: 'Home',      path: '/',          end: true },
  { name: 'Platform',  path: '/platform' },
  { name: 'Sectors',   path: '/sectors' },
  { name: 'Resources', path: '/resources' },
  { name: 'About',     path: '/about' },
  { name: 'Contact',   path: '/contact' },
  { name: 'Admin',     path: '/admin' },
];

const V2Nav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200/60 shadow-sm' : 'bg-white/60 backdrop-blur'
      }`}
      data-testid="v2-nav"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="v2-nav-logo">
            <div className="leading-tight">
              <div className="text-xl font-semibold text-slate-900 tracking-tight">Keystone</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium mt-0.5">Strategic Consultants</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.end}
                data-testid={`v2-nav-link-${l.name.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors relative ${
                    isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.name}
                    {isActive && <span className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-blue-600" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <BookDemoButton
              testid="v2-nav-book-demo"
              className="text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors cursor-pointer"
            >
              Book demo
            </BookDemoButton>
            <a
              href="https://demo.videra.app/explore"
             
              data-testid="v2-nav-cta"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              Try the Platform
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="v2-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6 text-slate-900" /> : <Menu className="w-6 h-6 text-slate-900" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 py-4 space-y-1" data-testid="v2-mobile-menu">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-2 text-base font-medium rounded ${
                    isActive ? 'text-slate-900 bg-slate-50' : 'text-slate-700 hover:bg-slate-50'
                  }`
                }
              >
                {l.name}
              </NavLink>
            ))}
            <div className="px-4 pt-3">
              <a
                href="https://demo.videra.app/explore"
               
                className="block w-full bg-slate-900 text-white text-center px-5 py-3 rounded-md text-sm font-medium"
              >
                Try the Platform
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

const V2Footer = () => (
  <footer className="bg-slate-950 text-slate-300 py-16" data-testid="v2-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
        <div>
          <div className="mb-4">
            <div className="text-xl font-semibold text-white tracking-tight">Keystone</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium mt-0.5">Strategic Consultants</div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            Specialist consultancy.<br />
            Videra is our flagship delivery platform.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Platform</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/platform" className="hover:text-white transition-colors">Overview</Link></li>
            <li><Link to="/solutions" className="hover:text-white transition-colors">Solutions</Link></li>
            <li><a href="https://demo.videra.app/explore" className="hover:text-white transition-colors">Try the demo</a></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Sectors</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/sectors/nhs" className="hover:text-white transition-colors">NHS</Link></li>
            <li><Link to="/sectors/hard-services" className="hover:text-white transition-colors">FM Hard Services</Link></li>
            <li><Link to="/sectors/construction" className="hover:text-white transition-colors">Construction</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Company</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
            <li><Link to="/resources" className="hover:text-white transition-colors">Resources</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between gap-4 text-xs text-slate-500">
        <p>© {new Date().getFullYear()} Keystone Strategic Consultants. All rights reserved.</p>
        <p className="flex items-center gap-4">
          <span>Videra is the flagship delivery platform from Keystone.</span>
          <a
            href="/admin"
            className="text-slate-600 hover:text-slate-400 transition-colors"
            data-testid="v2-footer-admin-link"
          >
            Admin
          </a>
        </p>
      </div>
    </div>
  </footer>
);

const V2Layout = () => {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  // Intercept all clicks on links to demo.videra.app:
  //  - Open in a NEW TAB so the user can always close it and return to Keystone
  //  - Append ?from=keystone&back=<current-url> so the demo app can render a back banner
  //  - Preserve middle/cmd/ctrl click (browser native behaviour) for power users
  useEffect(() => {
    const handler = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest('a[href*="demo.videra.app"]');
      if (!anchor) return;
      e.preventDefault();
      const href = anchor.getAttribute('href');
      const sep = href.includes('?') ? '&' : '?';
      const back = encodeURIComponent(window.location.href);
      const target = `${href}${sep}from=keystone&back=${back}`;
      window.open(target, '_blank', 'noopener,noreferrer');
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="v2-layout">
      <V2Nav />
      <div className="pt-20">
        <Breadcrumbs />
        <main>
          <Outlet />
        </main>
      </div>
      <V2Footer />
    </div>
  );
};

export default V2Layout;
